/* Vehicle feeds can be wrapped or paginated. Never mark a partial feed complete. */
(() => {
  if (window.AutoDiazVehicleData) return;
  const keys=['data','items','results','response','manufacturers','models','modelTypes','vehicles','engineTypes','categories','articles'];
  function extract(value,depth=0) {
    if(Array.isArray(value))return {found:true,rows:value};
    if(!value || typeof value!=='object' || depth>8)return {found:false,rows:[]};
    for(const key of keys)if(value[key] && typeof value[key]==='object') {
      const result=extract(value[key],depth+1);if(result.found)return result;
    }
    for(const item of Object.values(value))if(Array.isArray(item))return {found:true,rows:item};
    return {found:false,rows:[]};
  }
  function validate(value,depth=0) {
    if(!value || typeof value!=='object' || Array.isArray(value) || depth>8)return;
    if(value.error || value.success===false || value.status==='error' || (Array.isArray(value.errors) && value.errors.length))throw new Error('El catálogo no ha completado la respuesta');
    for(const key of ['data','response'])if(value[key] && !Array.isArray(value[key]))validate(value[key],depth+1);
  }
  const integer=value=>(typeof value==='number' || (typeof value==='string' && /^[+-]?\d+$/.test(value.trim()))) && Number.isInteger(Number(value))?Number(value):null;
  function pagination(body,requested,state,rowCount) {
    const containers=[body,body?.data,body?.response].filter(value=>value && typeof value==='object' && !Array.isArray(value));
    const candidates=containers.flatMap(value=>[value.pagination,value.pageInfo,value.meta,value]).filter(value=>value && typeof value==='object');
    const has=value=>['totalPages','pages','pageCount','lastPage','nextPage','hasNextPage','hasMore','totalItems','totalCount'].some(key=>Object.hasOwn(value,key)) || (Object.hasOwn(value,'total') && ['perPage','pageSize','limit','page','currentPage','pageNumber'].some(key=>Object.hasOwn(value,key)));
    const meta=candidates.find(has);
    if(!meta)return null;
    const current=integer(meta.currentPage ?? meta.pageNumber ?? meta.page) ?? requested ?? 1;
    if(requested!==null && current!==requested)throw new Error('El catálogo repitió una página');
    if(!state.started){state.zeroIndexed=current===0;state.started=true;}
    const count=integer(meta.totalItems ?? meta.totalCount ?? meta.total);
    const declaredSize=integer(meta.perPage ?? meta.pageSize ?? meta.limit);
    if(!state.size && declaredSize>0)state.size=declaredSize;
    if(!state.size && count!==null && rowCount>0)state.size=rowCount;
    const size=declaredSize>0?declaredSize:state.size;
    const total=integer(meta.totalPages ?? meta.pages ?? meta.pageCount) ?? (count!==null && size>0?Math.ceil(count/size):null),last=integer(meta.lastPage);
    const next=integer(meta.nextPage);
    if(count!==null && (count<0 || (state.count!==undefined && state.count!==count)))throw new Error('El tamaño del catálogo cambió');
    if(count!==null)state.count=count;
    if((total!==null && (total<0 || total>1000)) || (last!==null && (last<0 || last>1000)))throw new Error('Paginación del catálogo no válida');
    if(total===0){if(rowCount)throw new Error('Paginación del catálogo contradictoria');return null;}
    const end=last!==null?last:total!==null?(state.zeroIndexed?total-1:total):null;
    if(end!==null){if(state.end!==undefined && state.end!==end)throw new Error('La paginación del catálogo cambió');state.end=end;}
    const stop=meta.hasNextPage===false || meta.hasMore===false,more=meta.hasNextPage===true || meta.hasMore===true;
    if((stop && more) || (stop && end!==null && current<end) || (more && end!==null && current>=end) || (end!==null && current>end))throw new Error('Paginación del catálogo contradictoria');
    if(stop || (end!==null && current===end))return null;
    if(next!==null){if(next<=current)throw new Error('La paginación no avanza');return next;}
    if(end!==null)return current+1;
    if(meta.hasNextPage===true || meta.hasMore===true)return current+1;
    return null;
  }
  async function all(fetchPage,options={}) {
    const collected=[],seen=new Set(),state={started:false,zeroIndexed:false};let page=null,rounds=0;
    const concurrency=Math.max(1,Math.min(3,Number(options.concurrency)||1));
    try {
      do {
        const pages=[page];
        if(page!==null && state.end!==undefined)for(let offset=1;offset<concurrency && page+offset<=state.end;offset++)pages.push(page+offset);
        rounds+=pages.length;if(rounds>1000)throw new Error('El catálogo no termina la paginación');
        const bodies=await Promise.all(pages.map(requested=>fetchPage(requested)));
        for(let index=0;index<bodies.length;index++) {
          const body=bodies[index],requested=pages[index];validate(body);
          const parsed=extract(body);if(!parsed.found)throw new Error('Formato del catálogo no válido');
          const fingerprint=JSON.stringify(parsed.rows);
          if(seen.has(fingerprint) && requested!==null)throw new Error('El catálogo repitió los mismos resultados');
          seen.add(fingerprint);collected.push(...parsed.rows);
          const next=pagination(body,requested,state,parsed.rows.length);
          if((next!==null && !parsed.rows.length) || (next===null && (body?.complete===false || body?.data?.complete===false || body?.response?.complete===false || (state.count!==undefined && collected.length!==state.count))))throw new Error('La respuesta del catálogo está incompleta');
          if(index<bodies.length-1 && next!==pages[index+1])throw new Error('La paginación del catálogo no es continua');
          page=next;
        }
      }while(page!==null);
      return collected;
    } catch(error) {error.partialRows=collected;throw error;}
  }
  async function request(url,options={}) {
    // One retry for transient connections, with a fresh timeout per attempt.
    // A change of vehicle cancels the entire operation, including the retry.
    const signal=options.signal;
    const retries=options.retries===0?0:1;
    for(let attempt=0;attempt<=retries;attempt++) {
      if(signal?.aborted)throw new DOMException('Consulta cancelada','AbortError');
      const controller=new AbortController(),cancel=()=>controller.abort();
      signal?.addEventListener('abort',cancel,{once:true});
      const timer=setTimeout(cancel,options.timeoutMs || 20000);
      try {
        const response=await fetch(url,{headers:{Accept:'application/json'},signal:controller.signal,keepalive:Boolean(options.keepalive)});
        if(!response.ok) {
          const error=new Error('No se pudo conectar con el catálogo.');
          error.transient=[408,429,500,502,503,504].includes(response.status);
          throw error;
        }
        return await response.json();
      } catch(error) {
        if(signal?.aborted)throw new DOMException('Consulta cancelada','AbortError');
        const transient=error.transient || error.name==='AbortError' || error instanceof TypeError;
        if(attempt===retries || !transient)throw error;
        options.onRetry?.();
      } finally {clearTimeout(timer);signal?.removeEventListener('abort',cancel);}
    }
  }
  const feedPrefix='autodiazVehicleFeed:v1:',feeds=new Map(),pending=new Map(),feedTTL=86400000;
  function storedFeed(key) {
    let saved=feeds.get(key);
    if(!saved)try{saved=JSON.parse(localStorage.getItem(feedPrefix+key)||'null');}catch(_){}
    if(saved?.complete===true && Array.isArray(saved.rows) && Number.isFinite(saved.at) && saved.at<=Date.now() && Date.now()-saved.at<feedTTL)return saved.rows;
    feeds.delete(key);return null;
  }
  function saveFeed(key,rows) {
    const saved={complete:true,at:Date.now(),rows};feeds.delete(key);feeds.set(key,saved);
    while(feeds.size>24)feeds.delete(feeds.keys().next().value);
    try {
      const keys=[];for(let i=0;i<localStorage.length;i++){const name=localStorage.key(i);if(name.startsWith(feedPrefix) && name!==feedPrefix+key)keys.push(name);}
      while(keys.length>=8)localStorage.removeItem(keys.shift());
      localStorage.setItem(feedPrefix+key,JSON.stringify(saved));
    }catch(_){/* The selector also works when storage is full or unavailable. */}
  }
  function load(url,options={}) {
    const cancelled=()=>new DOMException('Consulta cancelada','AbortError');
    if(options.signal?.aborted)return Promise.reject(cancelled());
    const target=new URL(url);target.searchParams.delete('page');target.searchParams.sort();
    const key=target.toString(),cached=storedFeed(key);if(cached)return Promise.resolve(cached);
    let entry=pending.get(key);
    if(!entry || entry.controller.signal.aborted) {
      entry={controller:new AbortController(),consumers:new Set()};pending.set(key,entry);
      entry.promise=(async()=>{
        const timer=setTimeout(()=>entry.controller.abort(),options.deadlineMs||15000);
        try {
          const rows=await all(page=>{
            const u=new URL(target);if(page!==null)u.searchParams.set('page',String(page));
            return request(u,{signal:entry.controller.signal,timeoutMs:options.timeoutMs||8000,retries:0});
          },{concurrency:3});
          if(entry.controller.signal.aborted)throw cancelled();
          saveFeed(key,rows);return rows;
        }finally{clearTimeout(timer);entry.controller.abort();if(pending.get(key)===entry)pending.delete(key);}
      })();
    }
    return new Promise((resolve,reject)=>{
      const token={},signal=options.signal;entry.consumers.add(token);
      const clean=()=>{signal?.removeEventListener('abort',abort);entry.consumers.delete(token);};
      const abort=()=>{clean();reject(cancelled());if(!entry.consumers.size)entry.controller.abort();};
      signal?.addEventListener('abort',abort,{once:true});
      entry.promise.then(rows=>{clean();resolve(rows);},error=>{clean();reject(error);});
      if(signal?.aborted)abort();
    });
  }
  function supportedVehicle(vehicle) {
    const make=String(vehicle?.make||'').toLowerCase().replace(/[^a-z0-9]/g,'');
    return !(make==='landrover' && ['63','64'].includes(String(vehicle?.manufacturerId))) && !(make==='smart' && ['113','138'].includes(String(vehicle?.manufacturerId)));
  }
  function cacheKey(proxy,params,manufacturerId,vehicleId) {
    return 'autodiazCategories:v3:'+JSON.stringify([proxy,params.typeId,params.langId,params.countryFilterId,String(manufacturerId || ''),String(vehicleId || '')]);
  }
  function engineCodes(item) {
    const raw=item?.engineCodes ?? item?.engineCode ?? item?.motorCodes ?? item?.motorCode ?? [];
    const values=Array.isArray(raw)?raw:[raw];
    return [...new Set(values.flatMap(value=>typeof value==='object'?String(value?.engineCode ?? value?.code ?? ''):String(value || '')).flatMap(value=>value.split(/[,;/|]+/)).map(value=>value.trim()).filter(value=>/^[a-z0-9 .-]{2,25}$/i.test(value)))];
  }
  window.AutoDiazVehicleData={rows:value=>extract(value).rows,validate,all,request,load,supportedVehicle,cacheKey,engineCodes};
})();
